import moment from 'moment';
import { Department, IDepartment } from '../department';
import {
  FormSectionField,
  FormTemplate,
  IFormSectionField,
  IFormTemplate
} from '../form-template';
import { User } from '../user';
import {
  IEmployeeFormFieldSubmission,
  IEmployeeFormFieldSubmissionData,
  IEmployeeFormSubmission,
  IEmployeeFormSubmissionData,
  IEmployeeFormFieldOptionSubmission,
  IEmployeeFormFieldOptionSubmissionData,
  IEmployeeFormFieldSubmissionDataFieldErrorData,
  IEmployeeFormFieldSubmissionDataFieldError,
  IEmployeeFormFieldOptionSubmissionFieldErrorData,
  IEmployeeFormFieldOptionSubmissionFieldError,
  EmployeeFormSubmissionStateEnum,
  IEmployeeFormSubmissionStateInfoData,
  IEmployeeFormSubmissionChecklist,
  IEmployeeFormSubmissionChecklistData,
  IEmployeeFormSubmissionChecklistToDoTemplateData,
  IEmployeeFormSubmissionChecklistToDoTemplate,
  IEmployeeFormSubmissionComment,
  IEmployeeFormSubmissionCommentData,
  IEmployeeFormSubmissionCommentFieldError,
  IEmployeeFormSubmissionFieldError,
  IEmployeeFormSubmissionFieldErrorData,
  IEmployeeFormSubmissionCommentFieldErrorData,
  FormSubmissionChecklistStateEnum,
  IFormSubmissionChecklistStateInfo
} from './employee-form.types';
import { MentionCommentMixin } from '../mention';
import {
  AbstractAttachment,
  IAttachment,
  IAttachmentData
} from '../attachment';
import { AbstractMentionAttachmentComment } from '../comment';
export class EmployeeFormSubmission implements IEmployeeFormSubmission {
  readonly id?: number;
  readonly form: IFormTemplate | number;
  readonly answers?: IEmployeeFormFieldSubmission[];
  readonly state: EmployeeFormSubmissionStateEnum;
  readonly owner?: string;
  readonly employee: string;
  readonly created_at?: string | Date;
  readonly updated_at?: string | Date;

  constructor(data: IEmployeeFormSubmissionData) {
    if (data.id) {
      this.id = data.id;
    }

    if (typeof data.form == 'number') {
      this.form = data.form;
    } else {
      this.form = new FormTemplate(data.form);
    }

    this.state = data.state ?? EmployeeFormSubmissionStateEnum.Draft;

    if (data.answers) {
      this.answers = data.answers.map(
        data => new EmployeeFormFieldSubmission(data)
      );
    }

    this.owner = data.owner ?? '';

    this.employee = data.employee ?? '';

    if (data.created_at) {
      this.created_at = data.created_at ?? '';
    }
    if (data.updated_at) {
      this.updated_at = data.updated_at ?? '';
    }
  }
  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  getStateInfo(): IEmployeeFormSubmissionStateInfoData {
    // Returns color based on state
    switch (this.state) {
      case EmployeeFormSubmissionStateEnum.InReview:
        return { display_name: 'Submitted', color: 'submitted' };

      case EmployeeFormSubmissionStateEnum.InProgress:
        return { display_name: 'In Progress', color: 'submitted' };

      case EmployeeFormSubmissionStateEnum.Completed:
        return { display_name: 'Completed', color: 'approved' };

      case EmployeeFormSubmissionStateEnum.Cancelled:
        return { display_name: 'Cancelled', color: 'cancel' };

      default:
        return { display_name: 'Draft', color: 'draft' };
    }
  }
}

export class EmployeeFormFieldSubmission
  implements IEmployeeFormFieldSubmission {
  readonly id?: number;
  readonly value: string;
  readonly field: number | IFormSectionField;
  readonly option_submissions: IEmployeeFormFieldOptionSubmission[] | number[];

  constructor(data: IEmployeeFormFieldSubmissionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.value = data.value;

    if (typeof data.field == 'number') {
      this.field = data.field;
    } else {
      this.field = new FormSectionField(data.field);
    }

    this.option_submissions = (data.option_submissions as EmployeeFormFieldOptionSubmission[]).map(
      data =>
        new EmployeeFormFieldOptionSubmission(
          data as EmployeeFormFieldOptionSubmission
        )
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeFormFieldOptionSubmission
  implements IEmployeeFormFieldOptionSubmission {
  readonly id?: number;
  readonly option: number;

  constructor(data: IEmployeeFormFieldOptionSubmissionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.option = data.option;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeFormSubmissionChecklist
  implements IEmployeeFormSubmissionChecklist {
  readonly id?: number;
  readonly assignee: User | number;
  readonly todo_template: IEmployeeFormSubmissionChecklistToDoTemplate;
  readonly form_submission: number;
  readonly description: string;
  readonly state: FormSubmissionChecklistStateEnum =
    FormSubmissionChecklistStateEnum.InProgress;
  readonly completed_at: string | null;
  readonly completed_by: string | null;

  constructor(data: IEmployeeFormSubmissionChecklistData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_template = new EmployeeFormSubmissionChecklistToDoTemplate(
      data.todo_template
    );

    if (typeof data.assignee == 'number') {
      this.assignee = data.assignee;
    } else {
      this.assignee = new User(data.assignee);
    }

    this.form_submission = data.form_submission;
    this.description = data.description ?? '';

    if (data.state) {
      this.state = data.state;
    }

    this.completed_at = data.completed_at ?? null;
    this.completed_by = data.completed_by ?? null;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  getChecklistStateInfo(): IFormSubmissionChecklistStateInfo {
    return new FormSubmissionChecklistStateInfo({
      value: this.state
    }).getFormSubmissionChecklistState();
  }
}

export class FormSubmissionChecklistStateInfo
  implements IFormSubmissionChecklistStateInfo {
  readonly value: FormSubmissionChecklistStateEnum;
  readonly text?: string;
  readonly icon?: string;
  readonly color?: string;

  constructor(data: IFormSubmissionChecklistStateInfo) {
    this.value = data.value;
    if (data.text) {
      this.text = data.text;
    }
    if (data.icon) {
      this.icon = data.icon;
    }
    if (data.color) {
      this.color = data.color;
    }
  }

  static InProgress = new FormSubmissionChecklistStateInfo({
    icon: 'mdi-circle-outline',
    text: 'Mark To Do',
    color: 'grey darken-1',
    value: FormSubmissionChecklistStateEnum.InProgress
  });

  static NotApplicable = new FormSubmissionChecklistStateInfo({
    icon: 'mdi-minus-circle',
    text: 'Mark Not Applicable',
    color: 'error',
    value: FormSubmissionChecklistStateEnum.NotApplicable
  });

  static Completed = new FormSubmissionChecklistStateInfo({
    icon: 'mdi-check-circle',
    text: 'Mark Completed',
    color: 'success',
    value: FormSubmissionChecklistStateEnum.Completed
  });

  getFormSubmissionChecklistState(): IFormSubmissionChecklistStateInfo {
    let stateInfo = null;
    switch (this.value) {
      case FormSubmissionChecklistStateEnum.Completed:
        stateInfo = FormSubmissionChecklistStateInfo.Completed;
        break;

      case FormSubmissionChecklistStateEnum.NotApplicable:
        stateInfo = FormSubmissionChecklistStateInfo.NotApplicable;
        break;

      case FormSubmissionChecklistStateEnum.InProgress:
      default:
        stateInfo = FormSubmissionChecklistStateInfo.InProgress;
    }
    return stateInfo;
  }

  static getFormSubmissionChecklistStateInfoList(): IFormSubmissionChecklistStateInfo[] {
    return [
      FormSubmissionChecklistStateInfo.InProgress,
      FormSubmissionChecklistStateInfo.Completed,
      FormSubmissionChecklistStateInfo.NotApplicable
    ];
  }
}

export class EmployeeFormSubmissionChecklistToDoTemplate
  implements IEmployeeFormSubmissionChecklistToDoTemplate {
  readonly id?: number;
  readonly todo_text: string;
  readonly todo_department: number | IDepartment;

  constructor(data: IEmployeeFormSubmissionChecklistToDoTemplateData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_text = data.todo_text;

    if (typeof data.todo_department == 'number') {
      this.todo_department = data.todo_department;
    } else {
      this.todo_department = new Department(data.todo_department);
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeFormSubmissionCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}

export class EmployeeFormSubmissionComment
  extends AbstractMentionAttachmentComment
  implements IEmployeeFormSubmissionComment {
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];

  constructor(data?: IEmployeeFormSubmissionCommentData) {
    super(data);

    if (data) {
      if (data.text) {
        this.text = data.text;
      }

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new EmployeeFormSubmissionCommentAttachment(attachment)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.text;
  }
}
export class EmployeeFormSubmissiontFieldError
  implements IEmployeeFormSubmissionFieldError {
  readonly form: string[] = [];
  readonly owner: string[] = [];
  readonly answers: EmployeeFormFieldSubmissionDataFieldError[] = [] as EmployeeFormFieldSubmissionDataFieldError[];

  constructor(data?: IEmployeeFormSubmissionFieldErrorData) {
    if (data) {
      if (data.form) {
        this.form = data.form;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.answers) {
        this.answers = data.answers.map(
          answer => new EmployeeFormFieldSubmissionDataFieldError(answer)
        );
      }
    }
  }

  clearErrors(): void {
    this.form.splice(0, this.form.length);
    this.owner.splice(0, this.owner.length);
    this.answers.splice(0, this.answers.length);
  }

  static isEmployeeFormSubmissiontFieldError(
    data: IEmployeeFormSubmissionFieldErrorData
  ): boolean {
    if (data.form && Array.isArray(data.form)) {
      return true;
    } else if (data.owner && Array.isArray(data.owner)) {
      return true;
    } else if (data.answers && Array.isArray(data.answers)) {
      return true;
    }
    return false;
  }
}

export class EmployeeFormFieldSubmissionDataFieldError
  implements IEmployeeFormFieldSubmissionDataFieldError {
  readonly value: string[] = [];
  readonly field: string[] = [];
  readonly option_submissions: EmployeeFormFieldOptionSubmissionFieldError[] = [] as EmployeeFormFieldOptionSubmissionFieldError[];

  constructor(data?: IEmployeeFormFieldSubmissionDataFieldErrorData) {
    if (data) {
      if (data.value) {
        this.value = data.value;
      }
      if (data.field) {
        this.field = data.field;
      }
      if (data.option_submissions) {
        this.option_submissions = data.option_submissions.map(
          entitlement =>
            new EmployeeFormFieldOptionSubmissionFieldError(entitlement)
        );
      }
    }
  }

  clearErrors(): void {
    this.value.splice(0, this.value.length);
    this.field.splice(0, this.field.length);
    this.option_submissions.splice(0, this.field.length);
  }

  static isEmployeeFormFieldSubmissionDataFieldError(
    data: IEmployeeFormFieldSubmissionDataFieldErrorData
  ): boolean {
    if (data.value && Array.isArray(data.value)) {
      return true;
    } else if (data.field && Array.isArray(data.field)) {
      return true;
    } else if (
      data.option_submissions &&
      Array.isArray(data.option_submissions)
    ) {
      return true;
    }
    return false;
  }
}

export class EmployeeFormFieldOptionSubmissionFieldError
  implements IEmployeeFormFieldOptionSubmissionFieldError {
  readonly option: string[] = [];
  readonly non_field_errors: string[] = [];

  constructor(data?: IEmployeeFormFieldOptionSubmissionFieldErrorData) {
    if (data) {
      if (data.option) {
        this.option = data.option;
      }
      if (data.non_field_errors) {
        this.non_field_errors = data.non_field_errors;
      }
    }
  }

  clearErrors(): void {
    this.option.splice(0, this.option.length);
    this.non_field_errors.splice(0, this.non_field_errors.length);
  }

  static isEmployeeFormFieldOptionSubmissionFieldErro(
    data: IEmployeeFormFieldOptionSubmissionFieldErrorData
  ): boolean {
    if (data.option && Array.isArray(data.option)) {
      return true;
    }
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
      return true;
    }
    return false;
  }
}

export class EmployeeFormSubmissionCommentFieldError
  implements IEmployeeFormSubmissionCommentFieldError {
  readonly text: string[] = [];

  constructor(data: IEmployeeFormSubmissionCommentFieldErrorData) {
    if (data) {
      if (data.text) {
        this.text = data.text;
      }
    }
  }

  clearErrors(): void {
    this.text.splice(0, this.text.length);
  }
}
